import "./GroupPricing.css";
// import { Link } from "react-router-dom";
import TutorImage from '../Images/tutorEx.webp'
import React, { useState, useRef, useEffect } from "react";
import logger from "../Components/Logger";
import { LevelName } from "../Components/LevelName";

// Source code https://stackoverflow.com/questions/59595700/how-to-make-a-react-component-fade-in-on-scroll-using-intersectionobserver-but 
// + ChatGPT fixing small bugs in layout.
const FadeInSection = ({ children }) => {
  const domRef = useRef(null);
  const [isVisible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setVisible(true);
        observer.unobserve(domRef.current);
        logger.info("FadeInSection is now visible on the screen.");
      } else {
        logger.info("FadeInSection is not visible on the screen.");
      }
    });

    if (domRef.current) {
      observer.observe(domRef.current);
    } else {
      logger.warn("domRef.current is null. Cannot observe FadeInSection component.");
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={domRef}
      className={`fade-section ${isVisible ? "is-visible" : ""}`}
    >
      {children}
    </section>
  );
};

const CACHE_KEY = "a1app_pricing_cache";
const CACHE_TTL = 60 * 60 * 1000; // 1 hour

export function GroupPricing({ t }) {
  const [prices, setPrices] = useState(null);

  useEffect(() => {
    // ✅ Try localStorage cache first — no Firestore read
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_TTL) {
          logger.info("Pricing loaded from localStorage cache.");
          setPrices(data);
          return;
        }
      }
    } catch (_) {}

    // Cache miss or expired — fetch from server
    fetch("/.netlify/functions/getPricing")
      .then((res) => res.json())
      .then((data) => {
        setPrices(data);
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify({ data, timestamp: Date.now() }));
          logger.info("Pricing fetched and saved to localStorage cache.");
        } catch (_) {}
      })
      .catch((err) => {
        logger.error("Erreur chargement prix:", err);
      });
  }, []);

  return (
    <div className="groupPricingPage">
      <aside className="sidebarMenu">
        <section className="section">
          <div className="information-box">
            <h2 className="dashboardTitle">{t.GroupTutoringOverview}</h2>
          </div>
        </section>
        <div className="navigationSections">
          <div className="sidebarDivider"></div>
          <h4 className="weekDaysNav">Weekdays</h4>
          <button
            className="navButton"
            onClick={() => window.scrollTo({ top: 200, behavior: "smooth" })}>
            <span>📅</span> {t.Schedule}
          </button>
          <button
            className="navButton"
            onClick={() => window.scrollTo({ top: 1000, behavior: "smooth" })}>
            <span>👦</span> <LevelName level="elementary" />
          </button>
          <button
            className="navButton"
            onClick={() => window.scrollTo({ top: 1600, behavior: "smooth" })}>
            <span>🎓</span> <LevelName level="highschool" />
          </button>
          <div className="sidebarDivider"></div>
          <h4 className="weekEndNav">Weekend</h4>
          <button
            className="navButton"
            onClick={() => window.scrollTo({ top: 2200, behavior: "smooth" })}>
            <span>🎒</span> <LevelName level="elementary" />
          </button>
          <button
            className="navButton"
            onClick={() => window.scrollTo({ top: 2700, behavior: "smooth" })}>
            <span>🧑‍💻</span> <LevelName level="highschool" />
          </button>
        </div>
      </aside>

      <main className="mainPage">
        <div className="headerImage">
          <img src={TutorImage} alt="TutoringExample" />
        </div>
        <section className="tutoringHours">
          <FadeInSection>
            <h4>{t.GroupTutoringHours} 📚</h4>
            <hr className="scheduleDivider" />
            <p className="extraInfo">{t.MaxStudentsPerGroup}</p>
            <br />
            <div className="scheduleGrid">
              <div className="scheduleBox">
                <h4>{t.WeekdaysTitle} :</h4>
                <div className="navigationHours">
                  <p>{t.Monday}: 4:30 PM - 7:30 PM</p>
                  <p>{t.Tuesday}: 4:30 PM - 7:30 PM</p>
                  <p>{t.Thursday}: 4:30 PM - 7:30 PM</p>
                </div>
              </div>
              <div className="scheduleBox">
                <h4>{t.WeekendTitle} :</h4>
                <div className="navigationHours">
                  <p>{t.Saturday}: 11:00 AM - 1:00 PM</p>
                  <p>{t.Sunday}: 11:00 AM - 1:00 PM</p>
                </div>
              </div>
            </div>
          </FadeInSection>
        </section>

        <div className="titlePrice">
          <h2 className="pricingTitle">{t.TutoringPricesTitle}</h2>
          <p className="pricingSubtitle">{t.PricingSubtitle}</p>
        </div>
        <div className="pricingGrid">

          {/* --- WEEKDAYS --- */}
          <div className="pricingCard">
            <div className="pricingHeader">
              <h3><LevelName level="elementary" /> – {t.WeekdaysTitle}</h3>
              <p className="pricingPrice">
                <span className="currency">$</span>
                <span>{prices ? prices.primary_group_weekdays : "..."}</span>
                <small className="period"> / {t.Month}</small>
              </p>
            </div>
            <FadeInSection>
              <div className="pricingBody">
                <p className="pricingPlan">{t.BasicPlan}</p>
                <ul className="pricingList">
                  <li>{t.GroupTutoring}</li>
                  <li>{t.Monday}, {t.Tuesday}, {t.Thursday}</li>
                  <li>{t.HomeworkSupport}</li>
                </ul>
                <div className="pricingSubjects">
                  <h4>{t.TutoringSubjectsLabel}:</h4>
                  <p>{t.ElementarySubjectsList}</p>
                </div>
              </div>
            </FadeInSection>
          </div>

          <div className="pricingCard premium">
            <div className="pricingHeader">
              <h3><LevelName level="highschool" /> – {t.WeekdaysTitle}</h3>
              <p className="pricingPrice">
                <span className="currency">$</span>
                <span>{prices ? prices.secondary_group_weekdays : "..."}</span>
                <small className="period"> / {t.Month}</small>
              </p>
            </div>
            <FadeInSection>
              <div className="pricingBody">
                <p className="pricingPlan">{t.BasicPlan}</p>
                <ul className="pricingList">
                  <li>{t.GroupTutoring}</li>
                  <li>{t.Monday}, {t.Tuesday}, {t.Thursday}</li>
                  <li>{t.HighSchoolExamPrep}</li>
                </ul>
                <div className="pricingSubjects">
                  <h4>{t.TutoringSubjectsLabel}:</h4>
                  <p>{t.HighSchoolSubjectsList}</p>
                </div>
              </div>
            </FadeInSection>
          </div>

          {/* --- WEEKENDS --- */}
          <div className="pricingCard">
            <div className="pricingHeader">
              <h3><LevelName level="elementary" /> – <span className="weekend-textGroup">{t.WeekendTitle}</span></h3>
              <p className="pricingPrice">
                <span className="currency">$</span>
                <span>{prices ? prices.primary_group_weekends : "..."}</span>
                <small className="period"> / {t.Month}</small>
              </p>
            </div>
            <FadeInSection>
              <div className="pricingBody">
                <p className="pricingPlan">{t.BasicPlan}</p>
                <ul className="pricingList">
                  <li>{t.GroupTutoring}</li>
                  <li>{t.Saturday}, {t.Sunday}</li>
                  <li>{t.HomeworkSupport}</li>
                </ul>
                <div className="pricingSubjects">
                  <h4>{t.TutoringSubjectsLabel}:</h4>
                  <p>{t.ElementarySubjectsList}</p>
                </div>
              </div>
            </FadeInSection>
          </div>

          <div className="pricingCard premium">
            <div className="pricingHeader">
              <h3><LevelName level="highschool" /> – <span className="weekend-textGroup">{t.WeekendTitle}</span></h3>
              <p className="pricingPrice">
                <span className="currency">$</span>
                <span>{prices ? prices.secondary_group_weekends : "..."}</span>
                <small className="period"> / {t.Month}</small>
              </p>
            </div>
            <FadeInSection>
              <div className="pricingBody">
                <p className="pricingPlan">{t.BasicPlan}</p>
                <ul className="pricingList">
                  <li>{t.GroupTutoring}</li>
                  <li>{t.Saturday}, {t.Sunday}</li>
                  <li>{t.HighSchoolExamPrep}</li>
                </ul>
                <div className="pricingSubjects">
                  <h4>{t.TutoringSubjectsLabel}:</h4>
                  <p>{t.HighSchoolSubjectsList}</p>
                </div>
              </div>
            </FadeInSection>
          </div>

        </div>
      </main>
    </div>
  );
}